import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ModalV2Component } from "../../../../../shared/modal-v2/modal-v2.component";
import { DiaryFormElement } from "../../../../../core/models/trial-diary-form/diary-form-element.model";
import {
  DiaryFormElementVisibilityCondition
} from "../../../../../core/models/trial-diary-form/diary-form-element-visibility-condition.model";
import { SelectOption } from "../../../../../core/models/select-option.model";
import { DiaryFormElementType } from "../../../../../core/constants/diary-form-element-type.enum";

interface ConditionRow {
  id: string;
  form: FormGroup;
  sourceElementOptions: SelectOption[];
}

@Component({
  selector: 'app-element-visibility-modal',
  templateUrl: './element-visibility-modal.component.html',
  styleUrl: './element-visibility-modal.component.scss'
})
export class ElementVisibilityModalComponent {
  @ViewChild('modal') modal: ModalV2Component;

  @Output() applied = new EventEmitter<{
    elementIndex: number;
    defaultVisible: boolean;
    visibilityOperator: number | null;
    visibilityConditions: DiaryFormElementVisibilityCondition[];
  }>();

  protected elementIndex: number = 0;
  protected element: DiaryFormElement;
  protected allElements: DiaryFormElement[] = [];

  protected conditionRows: ConditionRow[] = [];

  protected settingsForm = new FormGroup({
    defaultVisible: new FormControl<string>('true'),
    visibilityOperator: new FormControl<string>('0')
  });

  protected visibilityOptions: SelectOption[] = [
    new SelectOption('true', 'Visible'),
    new SelectOption('false', 'Hidden')
  ];

  protected operatorOptions: SelectOption[] = [
    new SelectOption('0', 'AND'),
    new SelectOption('1', 'OR')
  ];

  protected sourceElementOptions: SelectOption[] = [];

  private readonly optionElementTypes: DiaryFormElementType[] = [
    DiaryFormElementType.Checkbox,
    DiaryFormElementType.Radio,
    DiaryFormElementType.Dropdown
  ];

  open(elementIndex: number, element: DiaryFormElement, allElements: DiaryFormElement[]) {
    this.elementIndex = elementIndex;
    this.element = element;
    this.allElements = allElements;

    this.settingsForm.patchValue({
      defaultVisible: element.defaultVisible.toString(),
      visibilityOperator: (element.visibilityOperator ?? 0).toString()
    });

    this.buildSourceElementOptions();
    this.buildConditionRows();

    this.modal.show();
  }

  private buildSourceElementOptions() {
    this.sourceElementOptions = this.allElements
      .filter((el, idx) =>
        idx < this.elementIndex &&
        this.optionElementTypes.includes(el.elementType) &&
        !el.deleted
      )
      .map(el => new SelectOption(el.id, el.label || `Question ${el.orderIndex + 1}`));
  }

  private buildConditionRows() {
    this.conditionRows = (this.element.visibilityConditions || []).map(vc => ({
      id: vc.id,
      form: new FormGroup({
        sourceElementId: new FormControl<string>(vc.sourceElementId),
        sourceOptionId: new FormControl<string>(vc.sourceOptionId)
      }),
      sourceElementOptions: this.getOptionsForElement(vc.sourceElementId)
    }));
  }

  getOptionsForElement(sourceElementId: string): SelectOption[] {
    if (!sourceElementId) return [];
    const sourceElement = this.allElements.find(el => el.id === sourceElementId);
    if (!sourceElement) return [];
    return sourceElement.options.map(opt => new SelectOption(opt.id, opt.text));
  }

  onSourceElementChanged(row: ConditionRow, value: string) {
    row.form.patchValue({ sourceElementId: value, sourceOptionId: '' });
    row.sourceElementOptions = this.getOptionsForElement(value);
  }

  addCondition() {
    this.conditionRows.push({
      id: '',
      form: new FormGroup({
        sourceElementId: new FormControl<string>(''),
        sourceOptionId: new FormControl<string>('')
      }),
      sourceElementOptions: []
    });
  }

  removeCondition(index: number) {
    this.conditionRows.splice(index, 1);
  }

  get defaultVisible(): boolean {
    return this.settingsForm.get('defaultVisible')?.value === 'true';
  }

  get isApplyDisabled(): boolean {
    if (!this.defaultVisible && this.conditionRows.length === 0) {
      return true;
    }

    for (const row of this.conditionRows) {
      const sourceElementId = row.form.get('sourceElementId')?.value;
      const sourceOptionId = row.form.get('sourceOptionId')?.value;
      if (!sourceElementId || !sourceOptionId) {
        return true;
      }
    }

    return false;
  }

  hide() {
    this.modal.hide();
  }

  onHandleApply() {
    if (this.isApplyDisabled) return;

    const conditions: DiaryFormElementVisibilityCondition[] = this.conditionRows.map(row =>
      new DiaryFormElementVisibilityCondition({
        id: row.id,
        elementId: this.element.id,
        sourceElementId: row.form.get('sourceElementId')?.value,
        sourceOptionId: row.form.get('sourceOptionId')?.value
      })
    );

    this.applied.emit({
      elementIndex: this.elementIndex,
      defaultVisible: this.defaultVisible,
      visibilityOperator: this.conditionRows.length > 0 ? +this.settingsForm.get('visibilityOperator')?.value : null,
      visibilityConditions: conditions
    });

    this.hide();
  }
}
